import { useState, useEffect } from 'react'
import { getLote, getGruposPlanta, accionLote } from '../../api/client'
import { miembrosDe, responsableTrasGrupo } from './otLogica'
import { hoyLocal } from '../partes/fechas'
import { formatFecha } from '../unidades/tipos'

const PRIORIDADES = ['critica', 'alta', 'media', 'baja']

// The admission tray's batch (L-<n>): its OTs waiting for admission, all
// chosen by default; one planning for the chosen ones -- or one reason to
// return them. Each OT goes through the engine as usual (all or none); an OT
// can be left out (unchecked) or opened and handled on its own.
export default function AdmisionLote({ loteId, t, onHecho, onVer }) {
  const [lote, setLote] = useState(null)
  const [grupos, setGrupos] = useState([])
  const [marcadas, setMarcadas] = useState(new Set())
  const [plan, setPlan] = useState(null)
  const [devolviendo, setDevolviendo] = useState(false)
  const [motivo, setMotivo] = useState('')
  const [faltan, setFaltan] = useState([])
  const [error, setError] = useState('')
  const [ocupado, setOcupado] = useState(false)

  useEffect(() => {
    Promise.all([getLote(loteId), getGruposPlanta()]).then(([l, gs]) => {
      setLote(l); setGrupos(gs)
      const espera = l.ots.filter(o => ['solicitada', 'propuesta'].includes(o.estado))
      setMarcadas(new Set(espera.map(o => o.id)))
      const o = espera[0]
      const g = o && gs.find(x => x.id === o.grupo_propuesto?.id)
      const hoy = hoyLocal()
      setPlan({
        prioridad: '', fecha_compromiso: o?.fecha_requerida && o.fecha_requerida >= hoy ? o.fecha_requerida : '',
        grupo_id: g ? String(g.id) : '', responsable_id: g ? responsableTrasGrupo(gs, g.id, null) : '', horas_estimadas: '',
      })
    }).catch(() => setError(t('ot.panel.noEncontrada')))
  }, [loteId, t])

  if (error && !lote) return <p className="text-sm text-red-600">{error}</p>
  if (!lote || !plan) return <p className="text-sm text-gray-400">{t('common:loading', { defaultValue: 'Cargando…' })}</p>

  const espera = lote.ots.filter(o => ['solicitada', 'propuesta'].includes(o.estado))
  const ids = espera.filter(o => marcadas.has(o.id)).map(o => o.id)
  const o0 = espera[0] || lote.ots[0]
  const set = (k, v) => { setPlan(p => ({ ...p, [k]: v })); setFaltan([]) }
  const elegirGrupo = (id) => { setPlan(p => ({ ...p, grupo_id: id, responsable_id: responsableTrasGrupo(grupos, id, p.responsable_id) })); setFaltan([]) }
  const marcar = (id) => setMarcadas(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })
  const todas = ids.length === espera.length

  const fallo = (err) => {
    const d = err.response?.data?.detail
    if (d?.code === 'validacion') {
      setFaltan(d.faltan || [])
      setError(`${d.ot ? `${d.ot}: ` : ''}${t('ot.faltanDatos', { lista: (d.faltan || []).map(f => t(`ot.faltan.${f}`, { defaultValue: f })).join(', ') })}`)
    } else setError(d?.message || t('ot.panel.fallo'))
  }
  const aprobar = async () => {
    const fs = []
    if (!plan.prioridad) fs.push('prioridad')
    if (!plan.fecha_compromiso) fs.push('fecha_compromiso')
    else if (plan.fecha_compromiso < hoyLocal()) fs.push('fecha_compromiso_pasada')
    if (!plan.grupo_id) fs.push('grupo')
    if (!plan.responsable_id) fs.push('responsable')
    if (fs.length) { setFaltan(fs); setError(t('ot.faltanDatos', { lista: fs.map(f => t(`ot.faltan.${f}`)).join(', ') })); return }
    setOcupado(true); setError('')
    try {
      onHecho(await accionLote(lote.id, 'aprobar_y_planificar', {
        ids, prioridad: plan.prioridad, fecha_compromiso: plan.fecha_compromiso, grupo_id: Number(plan.grupo_id),
        responsable_id: Number(plan.responsable_id), ...(plan.horas_estimadas !== '' ? { horas_estimadas: Number(plan.horas_estimadas) } : {}),
      }))
    } catch (err) { fallo(err) }
    setOcupado(false)
  }
  const devolver = async () => {
    if (!motivo.trim()) { setError(t('ot.admision.motivoRequerido')); return }
    setOcupado(true); setError('')
    try { onHecho(await accionLote(lote.id, 'devolver', { ids, motivo: motivo.trim() })) } catch (err) { fallo(err) }
    setOcupado(false)
  }

  const lbl = 'block text-xs font-medium text-gray-700 mb-1'
  const inp = (k) => `input ${faltan.includes(k) || (k === 'fecha_compromiso' && faltan.includes('fecha_compromiso_pasada')) ? '!border-rose-400' : ''}`

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold">{lote.codigo}</span>
          <h2 className="text-lg font-semibold text-gray-900">{t('ot.lote.nOt', { count: lote.ots.length })}</h2>
          {o0?.proyecto && <span className="text-sm text-gray-500">· {o0.proyecto.name}</span>}
        </div>
        <p className="text-sm text-gray-600 mt-1">
          {[lote.descripcion, o0?.fecha_requerida && t('ot.lote.requerida', { fecha: formatFecha(o0.fecha_requerida) }),
            o0?.grupo_propuesto && t('ot.lote.propone', { grupo: o0.grupo_propuesto.nombre }),
            o0?.solicitante?.display_name].filter(Boolean).join(' · ')}
        </p>
        <div className="mt-3 border border-gray-100 rounded-lg max-h-64 overflow-y-auto">
          <table className="w-full text-sm" data-anchos-propios>
            <thead className="sticky top-0 bg-gray-50">
              <tr className="text-left text-[11px] text-gray-500">
                <th className="w-8 px-2 py-1.5">
                  <input type="checkbox" checked={todas} aria-label={t('ot.lote.todas')}
                    onChange={() => setMarcadas(todas ? new Set() : new Set(espera.map(o => o.id)))} />
                </th>
                <th className="px-2 py-1.5">OT</th><th className="px-2 py-1.5">{t('ot.campos.articulo_codigo')}</th>
                <th className="px-2 py-1.5">{t('ot.campos.articulo_descripcion')}</th><th className="px-2 py-1.5 text-right">{t('ot.campos.cantidad')}</th>
              </tr>
            </thead>
            <tbody>
              {lote.ots.map(o => {
                const espera1 = ['solicitada', 'propuesta'].includes(o.estado)
                return (
                  <tr key={o.id} className={`border-t border-gray-50 ${espera1 ? '' : 'text-gray-400'}`}>
                    <td className="px-2 py-1">{espera1 && <input type="checkbox" checked={marcadas.has(o.id)} onChange={() => marcar(o.id)} />}</td>
                    <td className="px-2 py-1">
                      <button type="button" onClick={() => onVer(o.id)} className="font-mono text-xs hover:underline">{o.codigo}</button>
                      {!espera1 && <span className="ml-1 text-[10px]">({t(`ot.estados.${o.estado}`, { defaultValue: o.estado })})</span>}
                    </td>
                    <td className="px-2 py-1 font-mono text-xs">{o.articulo_codigo}</td>
                    <td className="px-2 py-1 truncate max-w-[220px]">{o.articulo_descripcion || '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{o.cantidad}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-gray-500 mt-1">{t('ot.lote.marcadas', { n: ids.length, total: espera.length })}</p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('ot.lote.planificacion')}</h3>
        <div className="space-y-3">
          <div>
            <span className={lbl}>{t('ot.campos.prioridad')} *</span>
            <div className={`inline-flex rounded-lg border p-0.5 ${faltan.includes('prioridad') ? 'border-rose-400' : 'border-gray-200'}`}>
              {PRIORIDADES.map(p => (
                <button key={p} type="button" onClick={() => set('prioridad', p)}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${plan.prioridad === p ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  {t(`ot.prioridades.${p}`)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className={lbl}>{t('ot.campos.fecha_compromiso')} *</label>
              <input type="date" min={hoyLocal()} className={inp('fecha_compromiso')} value={plan.fecha_compromiso}
                onChange={e => set('fecha_compromiso', e.target.value)} />
            </div>
            <div>
              <label className={lbl}>{t('ot.campos.horas_estimadas')}</label>
              <input type="number" min="0" step="0.5" className="input" value={plan.horas_estimadas}
                placeholder={t('ot.lote.horasPorOt')} onChange={e => set('horas_estimadas', e.target.value)} />
            </div>
            <div>
              <label className={lbl}>{t('ot.campos.grupo')} *</label>
              <select className={inp('grupo')} value={plan.grupo_id} onChange={e => elegirGrupo(e.target.value)}>
                <option value="">{t('ot.nueva.elegir')}</option>
                {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}{o0?.grupo_propuesto?.id === g.id ? ` · ${t('ot.admision.propuesto')}` : ''}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>{t('ot.campos.responsable')} *</label>
              <select className={inp('responsable')} value={plan.responsable_id} disabled={!plan.grupo_id} onChange={e => set('responsable_id', e.target.value)}>
                <option value="">{t('ot.nueva.elegir')}</option>
                {miembrosDe(grupos, plan.grupo_id).map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
              {!plan.grupo_id && <p className="text-[11px] text-gray-400 mt-1">{t('ot.planificar.primeroGrupo')}</p>}
            </div>
          </div>
          <p className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600">
            {t('ot.lote.seAplica', { n: ids.length })}
          </p>
        </div>

        {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

        {devolviendo ? (
          <div className="mt-4 space-y-2">
            <label className={lbl}>{t('ot.admision.motivo')} *</label>
            <textarea rows={2} autoFocus className="input" value={motivo} onChange={e => setMotivo(e.target.value)}
              placeholder={t('ot.admision.motivoPlaceholder')} />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setDevolviendo(false); setError('') }}
                className="border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">{t('common:actions.cancel')}</button>
              <button type="button" disabled={ocupado || !ids.length} onClick={devolver}
                className="bg-rose-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-rose-700 disabled:opacity-40">
                {t('ot.lote.devolver', { n: ids.length })}
              </button>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button type="button" disabled={!ids.length} onClick={() => setDevolviendo(true)}
              className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-40">
              {t('ot.lote.devolver', { n: ids.length })}
            </button>
            <button type="button" disabled={ocupado || !ids.length} onClick={aprobar}
              className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
              {ocupado ? t('common:actions.saving') : t('ot.lote.aprobar', { n: ids.length })}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
